import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Disclaimer from "@/components/Disclaimer";
import PetsTab from "@/components/PetsTab";
import PhotoTrailTab from "@/components/PhotoTrailTab";
import PhotoComparisonTab from "@/components/PhotoComparisonTab";
import DocSummary from "@/components/DocSummary";
import DeepReadPanel from "@/components/DeepReadPanel";
import NswAgentCheck from "@/components/NswAgentCheck";
import MobileBottomNav from "@/components/MobileBottomNav";
import AIThinking from "@/components/AIThinking";
import ContactsTab from "@/components/ContactsTab";
import LettersTab from "@/components/LettersTab";
import InboundForwardCard from "@/components/InboundForwardCard";
import DeadlinesWidget from "@/components/DeadlinesWidget";
import VoiceStatementModal from "@/components/VoiceStatementModal";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";
import VoiceNoteTimelineExtras from "@/components/VoiceNoteTimelineExtras";
import TenancyProgress from "@/components/TenancyProgress";
import RentLedgerTab from "@/components/RentLedgerTab";
import InspectionTab from "@/components/InspectionTab";
import { UpgradeNudge } from "@/components/UpgradeNudge";
import DisagreementsBanner from "@/components/DisagreementsBanner";
import BulkImportModal from "@/components/BulkImportModal";
import TimelineRuler from "@/components/TimelineRuler";
import TimelineGrid from "@/components/TimelineGrid";
import DisputeModeNudge from "@/components/DisputeModeNudge";
import LockedAction from "@/components/LockedAction";
import ForensicCheck from "@/components/ForensicCheck";
import InsightsPanel from "@/components/InsightsPanel";
import { CAPTURE_TIP } from "@/data/guideTips";
import DocumentGroupsPanel, { TAG_COLOR_STYLES } from "@/components/DocumentGroupsPanel";
import OnboardingWizard from "@/components/OnboardingWizard";
import RichText from "@/components/RichText";
import DocumentViewerModal from "@/components/DocumentViewerModal";
import { stateName, stateTribunal } from "@/lib/states";
import { toast } from "sonner";
import { Upload, FileText, Image as ImgIcon, Mic, Square, Send, Trash2, Clock, X, Loader2,
 Download, Calendar, ArrowLeft, Sparkles, FileSearch, PawPrint, Phone, Mail, Package, Receipt, Camera, ClipboardList, Files, ChevronDown, GitCompareArrows, Layers, CheckSquare, Square as SquareIcon, ShieldAlert, ClipboardCheck, Check, Highlighter
} from "lucide-react";

const DOC_TYPE_OPTIONS = [
 ["lease", "Lease / Tenancy Agreement"],
 ["bond_receipt", "Bond receipt (RTA / Authority)"],
 ["entry_report", "Entry Condition Report"],
 ["exit_report", "Exit Condition Report"],
 ["email", "Email"],
 ["screenshot", "Screenshot"],
 ["photo", "Photo"],
 ["invoice", "Invoice / Receipt"],
 ["notice", "Notice (breach / leave)"],
 ["other", "Other"],
];

const docTypeLabel = (t) => DOC_TYPE_OPTIONS.find(o => o[0] === t)?.[1] || t;

function CaseHeader({ c }) {
 const daysLeft = c.lease_end ? Math.ceil((new Date(c.lease_end) - new Date()) / 86400000) : null;
 return (
 <div className="card mb-6" data-testid="case-header">
 <div className="label-mono mb-2">{c.state} · Tenancy</div>
 <h1 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mb-1">{c.title}</h1>
 <p className="text-[#6B7280] mb-5 text-sm sm:text-base">{c.address}</p>
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm border-t border-[#E5E4DE] pt-5">
 <div><div className="label-mono mb-1">Lease</div><div className="font-mono text-[#1A1A1A]">{c.lease_start || ","} → {c.lease_end || ","}</div></div>
 <div><div className="label-mono mb-1">Days left</div><div className="font-mono text-[#1A1A1A]">{daysLeft !== null ? daysLeft : ","}</div></div>
 <div><div className="label-mono mb-1">Rent / wk</div><div className="font-mono text-[#1A1A1A]">${c.weekly_rent || ","}</div></div>
 <div><div className="label-mono mb-1">Bond</div><div className="font-mono text-[#1A1A1A]">${c.bond_amount || ","}</div></div>
 </div>
 {(c.landlord_name || c.agent_name || c.agency) && (
 <div className="mt-4 text-sm text-[#6B7280]">
 Landlord: <span className="text-[#1A1A1A]">{c.landlord_name || ","}</span> · Agent: <span className="text-[#1A1A1A]">{c.agent_name || ","}</span> {c.agency && `(${c.agency})`}
 {c.state === "NSW" && (
 <span className="ml-3 pl-3 border-l border-[#E5E4DE]">
 <NswAgentCheck initialQuery={c.agency || c.agent_name || c.landlord_name || ""} />
 </span>
 )}
 </div>
 )}
 <TenancyProgress leaseStart={c.lease_start} leaseEnd={c.lease_end} size="large" />
 <InboundForwardCard caseId={c.id} variant="compact" />
 <MoveOutSummaryCard caseId={c.id} state={c.state} />
 </div>
 );
}

/**
 * Move-Out Mode summary card surfaced on the case overview tab. Always
 * visible: every case will hit move-out eventually and we want renters to
 * discover the flow long before the dispute starts. Renders a tiny status
 * line plus a CTA into the dedicated workspace page.
 */
function MoveOutSummaryCard({ caseId, state }) {
 const { user } = useAuth();
 const [flagCount, setFlagCount] = useState(null);
 const [lastAt, setLastAt] = useState(null);
 useEffect(() => {
 let alive = true;
 api.get(`/cases/${caseId}/move-out`)
 .then(({ data }) => {
 if (!alive) return;
 setFlagCount(data?.session?.flag_count ?? 0);
 setLastAt(data?.session?.last_analyzed_at || null);
 })
 .catch(() => {});
 return () => { alive = false; };
 }, [caseId]);
 const hasRun = !!lastAt;
 return (
 <Link
 to={`/cases/${caseId}/move-out`}
 className="block border-l-2 border-[#B35D4C] bg-[#FFF7F2] hover:bg-[#FFEFE4] transition-colors px-4 py-3"
 data-testid="moveout-summary-card"
 >
 <div className="flex items-start gap-3">
 <div className="w-9 h-9 bg-[#B35D4C]/10 border border-[#B35D4C]/30 flex items-center justify-center flex-shrink-0">
 <ShieldAlert size={16} strokeWidth={1.75} className="text-[#B35D4C]" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#B35D4C] mb-0.5">
 Move-Out Mode · Bond protection
 </div>
 <div className="font-serif text-[15px] text-[#0D2B52] leading-tight">
 {hasRun
 ? (flagCount === 0
 ? "We checked your move-out paperwork. Nothing questionable yet."
 : `We found ${flagCount} flag${flagCount === 1 ? "" : "s"} worth a closer look.`)
 : "Approaching the end of your tenancy? Run a free bond-protection check."}
 </div>
 <div className="text-[11.5px] text-[#6B7280] mt-0.5 leading-snug">
 {state ? `${state}-aware. ` : ""}Reads your entry report, lease, exit paperwork and photos. {user?.tier === "free" ? "Detailed flags unlock with Dispute Mode." : "Full detail unlocked."}
 </div>
 </div>
 <span className="text-[11px] font-mono text-[#B35D4C] uppercase tracking-[0.12em] flex-shrink-0 mt-1">
 Open →
 </span>
 </div>
 </Link>
 );
}

function DocumentsTab({ caseId, docs, reload, intent, clearIntent }) {
 const { user } = useAuth();
 const [uploading, setUploading] = useState(false);
 const [docType, setDocType] = useState("other");
 const [eventDate, setEventDate] = useState("");
 const [bulkResult, setBulkResult] = useState(null); // shown after multi-file upload
 // Soft dispute-mode upsell. Triggered when a free user uploads a file
 // whose name smells like an active dispute (notice to leave, eviction,
 // tribunal). Session-scoped so we never nag — once dismissed, it stays
 // gone for the rest of the browser tab session.
 const [showDisputeUploadNudge, setShowDisputeUploadNudge] = useState(false);
 const DISPUTE_FILENAME_KEYWORDS = [
 "notice to leave", "notice to vacate", "eviction", "evict",
 "tribunal", "qcat", "ncat", "vcat", "breach", "termination",
 "bond claim", "bond return", "form 12", "form 11", "form 16",
 ];
 const maybeTriggerDisputeNudge = (files) => {
 if (!user || user.tier !== "free") return;
 try {
 if (sessionStorage.getItem("tt_dispute_upload_nudge_dismissed") === "1") return;
 } catch { /* ignore */ }
 const names = Array.from(files || []).map((f) => (f.name || "").toLowerCase());
 if (names.some((n) => DISPUTE_FILENAME_KEYWORDS.some((kw) => n.includes(kw)))) {
 setShowDisputeUploadNudge(true);
 }
 };
 const dismissDisputeNudge = () => {
 setShowDisputeUploadNudge(false);
 try { sessionStorage.setItem("tt_dispute_upload_nudge_dismissed", "1"); } catch { /* ignore */ }
 };
 const fileRef = useRef();
 // Multi-select & grouping state. Live in this component (no global store).
 // selectMode: master toggle to show checkboxes. selected: Set of doc ids.
 // groups: current list of DocGroup for this case (sourced from the panel
 // so the doc cards can render the chips). groupsRefresh: bump to reload.
 const [selectMode, setSelectMode] = useState(false);
 const [selected, setSelected] = useState(new Set());
 const [groups, setGroups] = useState([]);
 const [groupsRefresh, setGroupsRefresh] = useState(0);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupColor, setGroupColor] = useState("teal");
  const [creatingGroup, setCreatingGroup] = useState(false);
  // Active tag filter. null = show everything. Applies to both the document
  // list and the timeline so the two views stay in sync.
  const [activeTagId, setActiveTagId] = useState(null);
  // Document viewer / highlighter. Null when closed.
  const [viewerDoc, setViewerDoc] = useState(null);

 const toggleSelect = (id) => {
 setSelected((prev) => {
 const next = new Set(prev);
 if (next.has(id)) next.delete(id); else next.add(id);
 return next;
 });
 };

 const clearSelection = () => { setSelected(new Set()); setSelectMode(false); };

 const createGroup = async () => {
 const name = (groupName || "").trim();
 if (!name) { toast.error("Give the group a name."); return; }
 if (selected.size === 0) { toast.error("Select at least one document."); return; }
 setCreatingGroup(true);
 try {
 await api.post(`/cases/${caseId}/groups`, {
 name,
 doc_ids: Array.from(selected),
 color: groupColor,
 });
 toast.success(`Group "${name}" created with ${selected.size} document${selected.size === 1 ? "" : "s"}.`);
 setGroupModalOpen(false);
 setGroupName("");
 setGroupColor("teal");
 clearSelection();
 setGroupsRefresh((n) => n + 1);
 } catch (e) {
 toast.error(e.response?.data?.detail || "Couldn't create group.");
 } finally {
 setCreatingGroup(false);
 }
 };

 // Reverse index: doc_id -> [group, ...] so each doc card can show its chips.
 const groupsByDoc = useMemo(() => {
 const m = {};
 for (const g of groups) {
 for (const id of (g.doc_ids || [])) {
 if (!m[id]) m[id] = [];
 m[id].push(g);
 }
 }
 return m;
 }, [groups]);

 // Docs visible in the vault. Filtered down to the active tag when one is
 // set. Also drives the timeline and the "Download tagged bundle" button.
 const visibleDocs = useMemo(() => {
   if (!activeTagId) return docs;
   const grp = groups.find((g) => g.id === activeTagId);
   if (!grp) return docs;
   const memberIds = new Set(grp.doc_ids || []);
   return docs.filter((d) => memberIds.has(d.id));
 }, [docs, groups, activeTagId]);
 const activeTag = useMemo(
   () => (activeTagId ? groups.find((g) => g.id === activeTagId) : null),
   [activeTagId, groups],
 );

 // "Download tagged bundle" — asks the backend for an Evidence PDF that
 // only contains the docs in this tag. Reuses the /evidence-bundle
 // endpoint with the new `group_id` form param. Handles the 402 upgrade
 // gate the same way the main bundle download does.
 const downloadBundle = async ({ groupId, name }) => {
   try {
     const fd = new FormData();
     fd.append("group_id", groupId);
     const res = await api.post(`/cases/${caseId}/evidence-bundle`, fd, {
       responseType: "blob",
       validateStatus: (s) => s === 402 || (s >= 200 && s < 300),
     });
     if (res.status === 402) {
       let detail = "Evidence bundles are part of paid plans. Upgrade to download a tribunal-ready PDF.";
       try {
         const text = await res.data.text();
         const parsed = JSON.parse(text);
         if (parsed.detail) detail = parsed.detail;
       } catch { /* ignore */ }
       toast.error("Bundle PDF needs a paid plan", {
         description: detail,
         action: { label: "See plans", onClick: () => { window.location.href = "/pricing"; } },
         duration: 9000,
       });
       return;
     }
     const url = window.URL.createObjectURL(res.data);
     const a = document.createElement("a");
     a.href = url;
     const cd = res.headers["content-disposition"] || "";
     const m = cd.match(/filename="?([^";]+)"?/);
     const slug = (name || "tag").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
     a.download = m ? m[1] : `TenantTrail_${slug}_bundle.pdf`;
     document.body.appendChild(a);
     a.click();
     document.body.removeChild(a);
     window.URL.revokeObjectURL(url);
     toast.success(`Tagged bundle "${name}" ready.`);
   } catch (e) {
     toast.error(e.response?.data?.detail || "Couldn't build the tagged bundle. Make sure the tag has at least one document.");
   }
 };


 // Honour an inbound intent (e.g. an Insight click. "Add bond receipt").
 // Preselect the doc_type and, if a file picker is available, gently flash
 // the upload area so the user sees where to drop the file.
 useEffect(() => {
 if (intent?.doc_type) {
 setDocType(intent.doc_type);
 const el = document.querySelector('[data-testid="doc-upload-card"]');
 if (el) {
 el.classList.add("ring-2", "ring-[#1B6C62]");
 setTimeout(() => el.classList.remove("ring-2", "ring-[#1B6C62]"), 2200);
 }
 clearIntent?.();
 }
 }, [intent, clearIntent]);

 // Compute annex numbers (chronological)
 const annexed = [...docs]
.sort((a, b) => (a.event_date || a.created_at || "").localeCompare(b.event_date || b.created_at || ""))
.map((d, i) => ({...d, annex: i + 1 }));
 const byId = Object.fromEntries(annexed.map(d => [d.id, d.annex]));

  // Tag-this-upload prompt state. After every upload we auto-tag the new
  // docs as "General" (creating the tag if missing) then surface a small
  // banner offering the user a chance to override the tag before it fades.
  // Keeps tagging frictionless without hijacking their flow.
  const [pendingTagDocs, setPendingTagDocs] = useState([]); // [{id, name}]
  const [currentTagId, setCurrentTagId] = useState(null); // tag those docs currently sit in

  // Best-effort helper: ensure the "General" tag exists, return its id.
  const ensureGeneralTag = async () => {
    const existing = groups.find((g) => g.name.toLowerCase() === "general");
    if (existing) return existing;
    try {
      const { data: fresh } = await api.post(`/cases/${caseId}/groups`, {
        name: "General", color: "slate",
      });
      // Silently sync local groups so UI reflects it immediately.
      setGroups((prev) => [...prev, fresh]);
      return fresh;
    } catch {
      return null;
    }
  };

  // Add a set of docs to a tag. Silently ignores duplicates the backend rejects.
  const addDocsToTag = async (tagId, docIds) => {
    for (const docId of docIds) {
      try {
        await api.post(`/cases/${caseId}/groups/${tagId}/docs/${docId}`);
      } catch { /* already in the tag or 404 - move on */ }
    }
    // Refresh groups so counts update and the freshly-added doc's chips render.
    try {
      const { data } = await api.get(`/cases/${caseId}/groups`);
      setGroups(data || []);
    } catch { /* ignore */ }
  };

  // Auto-tag newly uploaded docs as "General" and open the override banner.
  const openTagPromptFor = async (newDocs) => {
    if (!newDocs || newDocs.length === 0) return;
    const generalTag = await ensureGeneralTag();
    if (!generalTag) return;
    await addDocsToTag(generalTag.id, newDocs.map((d) => d.id));
    setPendingTagDocs(newDocs.map((d) => ({ id: d.id, name: d.original_name || d.title || d.filename || "Document" })));
    setCurrentTagId(generalTag.id);
  };

  // User picked a different tag from the banner chips. Move docs out of
  // "General" and into the chosen tag. If the chosen tag is General itself,
  // just dismiss.
  const rebindPendingToTag = async (nextTag) => {
    if (!nextTag) return;
    const docIds = pendingTagDocs.map((d) => d.id);
    if (nextTag.id !== currentTagId && currentTagId) {
      // Remove from current tag first so multi-tag noise doesn't accumulate.
      for (const docId of docIds) {
        try {
          await api.delete(`/cases/${caseId}/groups/${currentTagId}/docs/${docId}`);
        } catch { /* ignore */ }
      }
    }
    await addDocsToTag(nextTag.id, docIds);
    setCurrentTagId(nextTag.id);
    toast.success(`Tagged as "${nextTag.name}"`);
    setPendingTagDocs([]);
  };

 // One handler. if 1 file: classic single upload with the form fields above.
 // If >1 files: route through the bulk endpoint, then show the review modal.
 const onFiles = async (files) => {
 if (!files || files.length === 0) return;
 if (files.length === 1) {
 await uploadSingle(files[0]);
 } else {
 await uploadMany(files);
 }
 };

 const uploadSingle = async (file) => {
 setUploading(true);
 maybeTriggerDisputeNudge([file]);
 try {
 const fd = new FormData();
 fd.append("file", file);
 fd.append("doc_type", docType);
 if (eventDate) fd.append("event_date", eventDate);
 const { data: newDoc } = await api.post(`/cases/${caseId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
 reload();
 toast.success("Document saved", { description: `${file.name} added to your chronology` });
 // Auto-tag as General and offer a change chip.
 if (newDoc && newDoc.id) {
   openTagPromptFor([newDoc]);
 }
 } catch (e) {
 toast.error("Upload failed", { description: e.response?.data?.detail || "Try again in a moment" });
 } finally {
 setUploading(false);
 if (fileRef.current) fileRef.current.value = "";
 }
 };

 const uploadMany = async (files) => {
 setUploading(true);
 maybeTriggerDisputeNudge(files);
 try {
 const fd = new FormData();
 for (const f of files) fd.append("files", f);
 const { data } = await api.post(`/cases/${caseId}/documents/bulk`, fd, {
 headers: { "Content-Type": "multipart/form-data" },
 timeout: 600000,
 });
 await reload();
 setBulkResult(data);
 toast.success(`${files.length} documents saved`, { description: "All files imported and indexed" });
 // Auto-tag the whole batch as General and offer a change chip.
 if (Array.isArray(data?.created) && data.created.length > 0) {
   openTagPromptFor(data.created);
 }
 } catch (e) {
 toast.error("Bulk upload failed", { description: e.response?.data?.detail || "Try again in a moment" });
 } finally {
 setUploading(false);
 if (fileRef.current) fileRef.current.value = "";
 }
 };

 const remove = async (id) => {
 if (!window.confirm("Delete this document?")) return;
 await api.delete(`/documents/${id}`);
 reload();
 toast.success("Document removed");
 };

 const download = async (d) => {
 // Treat 402 as a normal response so axios doesn't reject and trigger the
 // CRA dev-overlay. The toast UX is identical either way.
 const res = await api.get(`/documents/${d.id}/download`, {
 responseType: "blob",
 validateStatus: (s) => s === 402 || (s >= 200 && s < 300),
 }).catch(() => null);
 if (!res) {
 toast.error("Couldn't download right now. Please try again.");
 return;
 }
 if (res.status === 402) {
 toast.error("Downloads need a paid plan", {
 description: "Upgrade to Vault, Compass, or Pro to save documents out of TenantTrail.",
 action: { label: "See plans", onClick: () => { window.location.href = "/pricing"; } },
 duration: 8000,
 });
 return;
 }
 const url = window.URL.createObjectURL(res.data);
 const a = document.createElement("a");
 a.href = url; a.download = d.original_name; a.click();
 window.URL.revokeObjectURL(url);
 };

 return (
 <div className="space-y-6" data-testid="documents-tab">
 {showDisputeUploadNudge && (
 <DisputeModeNudge
 variant="upload"
 onDismiss={dismissDisputeNudge}
 testid="upload-dispute-nudge"
 />
 )}
 <div className="card transition-all" data-testid="doc-upload-card">
 <div className="mb-4">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Add documents</h3>
 <p className="text-xs text-[#6B7280]">One file or a whole pile. Drop them all in. We'll read each one and slot it into your chronology.</p>
 </div>
 <div className="grid sm:grid-cols-3 gap-4 mb-4">
 <div><label className="label-mono block mb-1.5">Document type <span className="normal-case text-[#6B7280] font-sans">(for a single file)</span></label>
 <select className="input-field" value={docType} onChange={e=>setDocType(e.target.value)} data-testid="upload-doctype">
 {DOC_TYPE_OPTIONS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
 </select></div>
 <div className="sm:col-span-2"><label className="label-mono block mb-1.5">Date this happened <span className="normal-case text-[#6B7280] font-sans">(optional)</span></label>
 <input type="date" className="input-field" value={eventDate} onChange={e=>setEventDate(e.target.value)} data-testid="upload-date" /></div>
 </div>
 <div
 className="border-2 border-dashed border-[#E5E4DE] bg-[#FAF9F6] hover:border-[#0D2B52]/50 hover:bg-[#0D2B52]/5 p-10 text-center cursor-pointer transition-colors"
 onClick={() => fileRef.current?.click()}
 onDragOver={(e) => { e.preventDefault(); }}
 onDrop={(e) => { e.preventDefault(); onFiles(Array.from(e.dataTransfer.files || [])); }}
 data-testid="upload-zone"
 >
 <Upload size={28} strokeWidth={1.5} className="mx-auto text-[#0D2B52] mb-3" />
 <p className="font-serif text-lg text-[#1A1A1A] mb-1">{uploading ? "Reading your files…" : "Drop one or many files here"}</p>
 <p className="text-xs text-[#6B7280]">PDFs, photos, screenshots, emails (.eml). Max 25 MB per file.</p>
 <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => onFiles(Array.from(e.target.files || []))} data-testid="upload-input" />
 </div>
 </div>

 {/* Capture-what-we-can't-reach tip, encourages screenshotting Tapi / PropertyMe updates. */}
 <div
 className="border-l-4 border-l-[#C9A961] bg-[#FAF6E8] p-4 flex items-start gap-3"
 data-testid="capture-tip-card"
 >
 <div className="w-8 h-8 flex items-center justify-center bg-white border border-[#C9A961]/40 flex-shrink-0">
 <Camera size={14} strokeWidth={1.75} className="text-[#8A6D3B]" />
 </div>
 <div className="flex-1 min-w-0">
 <h4 className="font-serif text-[15px] text-[#0D2B52] leading-snug mb-1">{CAPTURE_TIP.title}</h4>
 <p className="text-[13px] text-[#1A1A1A] font-serif leading-relaxed mb-2">
 {CAPTURE_TIP.body[0]}
 </p>
 <p className="text-[13px] text-[#1A1A1A] font-serif leading-relaxed">
 {CAPTURE_TIP.body[1]}
 </p>
 <Link
 to={`/guide#${CAPTURE_TIP.id}`}
 className="inline-flex items-center gap-1 mt-2.5 text-[12px] text-[#8A6D3B] hover:text-[#0D2B52] font-mono"
 data-testid="capture-tip-link"
 >
 See the four-step checklist <ArrowLeft size={11} strokeWidth={1.5} className="rotate-180" />
 </Link>
 </div>
 </div>

          <div className="space-y-3" data-testid="documents-list">
            {/* Groups panel + selection toolbar header */}
            <DocumentGroupsPanel
              caseId={caseId}
              docs={docs}
              refreshKey={groupsRefresh}
              onGroupsChange={setGroups}
            />
            {/* Just-uploaded tag prompt. Auto-tagged as "General"; user can
                override with a quick chip click before the banner is dismissed. */}
            {pendingTagDocs.length > 0 && (
              <div
                className="bg-[#FFF9E9] border border-[#C9A961]/50 px-4 py-3"
                data-testid="tag-prompt-banner"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-[13px] text-[#1A1A1A] font-serif leading-snug">
                    <strong>Tagged {pendingTagDocs.length === 1 ? "as" : `${pendingTagDocs.length} docs as`} &ldquo;General&rdquo;.</strong>{" "}
                    <span className="text-[#6B7280]">Change tag if you like.</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingTagDocs([])}
                    className="text-[11px] font-mono uppercase tracking-wider text-[#6B7280] hover:text-[#0D2B52]"
                    data-testid="tag-prompt-keep"
                  >
                    Keep General
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {groups
                    .filter((g) => g.name.toLowerCase() !== "general")
                    .map((g) => {
                      const styles = TAG_COLOR_STYLES[g.color] || TAG_COLOR_STYLES.teal;
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => rebindPendingToTag(g)}
                          className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2 py-1 border border-[#E5E4DE] bg-white hover:border-[#0D2B52] transition-colors"
                          data-testid={`tag-prompt-chip-${g.id}`}
                        >
                          <span className={`w-2 h-2 ${styles.dot}`} />
                          {g.name}
                        </button>
                      );
                    })}
                  <button
                    type="button"
                    onClick={() => {
                      const name = window.prompt("Name this tag");
                      if (!name) return;
                      // Create tag, then rebind pending docs into it. Uses the
                      // same flow as manual tag creation.
                      api.post(`/cases/${caseId}/groups`, { name: name.trim(), color: "teal" })
                        .then(({ data: created }) => {
                          setGroups((prev) => [...prev, created]);
                          rebindPendingToTag(created);
                        })
                        .catch(() => toast.error("Couldn't create tag. Try again."));
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-1 border border-dashed border-[#0D2B52] text-[#0D2B52] hover:bg-[#0D2B52] hover:text-white transition-colors"
                    data-testid="tag-prompt-new-tag"
                  >
                    + New tag
                  </button>
                </div>
              </div>
            )}

            {/* Tag filter bar. Visible whenever the user has at least one tag. */}
            {groups.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-1.5 py-2 border-y border-[#E5E4DE] bg-[#FAF9F6]/60 px-2"
                data-testid="tag-filter-bar"
              >
                <span className="label-mono text-[#6B7280] mr-1">Filter</span>
                <button
                  type="button"
                  onClick={() => setActiveTagId(null)}
                  className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors ${
                    activeTagId === null
                      ? "bg-[#0D2B52] text-white border-[#0D2B52]"
                      : "bg-white text-[#0D2B52] border-[#E5E4DE] hover:border-[#0D2B52]"
                  }`}
                  data-testid="tag-filter-all"
                >
                  All ({docs.length})
                </button>
                {groups.map((g) => {
                  const styles = TAG_COLOR_STYLES[g.color] || TAG_COLOR_STYLES.teal;
                  const active = activeTagId === g.id;
                  const count = (g.doc_ids || []).length;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setActiveTagId(active ? null : g.id)}
                      className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors ${
                        active
                          ? `${styles.pill} border-transparent`
                          : "bg-white text-[#1A1A1A] border-[#E5E4DE] hover:border-[#0D2B52]"
                      }`}
                      title={`${g.name} · ${count} doc${count === 1 ? "" : "s"}`}
                      data-testid={`tag-filter-${g.id}`}
                    >
                      <span className={`w-2 h-2 ${styles.dot} ${active ? "ring-1 ring-white/70" : ""}`} />
                      {g.name} ({count})
                    </button>
                  );
                })}
                {activeTag && (
                  <button
                    type="button"
                    onClick={() => downloadBundle({ groupId: activeTag.id, name: activeTag.name })}
                    className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 bg-[#1B6C62] text-white hover:brightness-95 transition-all"
                    data-testid="tag-filter-download-bundle"
                    title="Download an evidence bundle PDF containing only the docs in this tag"
                  >
                    <Download size={11} strokeWidth={2} />
                    Download tagged bundle
                  </button>
                )}
              </div>
            )}
            {docs.length > 0 && (
 <div className="flex items-center justify-between gap-2 py-1 px-1">
 <div className="text-xs font-mono text-[#6B7280]">
 {activeTag
   ? `${visibleDocs.length} of ${docs.length} document${docs.length === 1 ? "" : "s"} · tag: ${activeTag.name}`
   : `${docs.length} document${docs.length === 1 ? "" : "s"}`}
 {selectMode && selected.size > 0 ? ` · ${selected.size} selected` : ""}
 </div>
 {selectMode ? (
 <div className="flex items-center gap-2">
 <button
 onClick={() => {
 if (docs.length === selected.size) clearSelection();
 else setSelected(new Set(docs.map((d) => d.id)));
 }}
 className="btn-ghost text-xs"
 data-testid="docs-select-all"
 >
 {docs.length === selected.size ? "Clear all" : "Select all"}
 </button>
 <button
 onClick={clearSelection}
 className="btn-ghost text-xs"
 data-testid="docs-select-cancel"
 >
 Done
 </button>
 </div>
 ) : (
 <button
 onClick={() => setSelectMode(true)}
 className="btn-ghost text-xs inline-flex items-center gap-1.5"
 data-testid="docs-select-toggle"
 title="Select multiple to group them"
 >
 <CheckSquare size={13} strokeWidth={1.75} /> Select &amp; group
 </button>
 )}
 </div>
 )}
 {docs.length === 0 ? (
 <div className="card text-center py-12 bg-[#FAF9F6]">
 <FileText size={28} strokeWidth={1.5} className="mx-auto text-[#1B6C62] mb-3 opacity-70" />
 <p className="font-serif text-lg text-[#0D2B52] mb-1">Start with your lease</p>
 <p className="text-sm text-[#6B7280] max-w-sm mx-auto mb-4">Drop your tenancy agreement above. TenantTrail reads the dates, parties and bond amount automatically. Then add anything else as you go.</p>
 <div className="text-xs text-[#6B7280] max-w-md mx-auto bg-white border border-[#E5E4DE] p-4 text-left">
 <div className="label-mono mb-2 text-[#0D2B52]">Good things to upload, in order</div>
 <ul className="space-y-1 font-serif text-[13px] text-[#1A1A1A] leading-relaxed">
 <li>1. Signed lease / tenancy agreement (PDF)</li>
 <li>2. Bond receipt from the RTA / Bond Authority</li>
 <li>3. Entry condition report (with photos)</li>
 <li>4. Any agent emails or notices you've already received</li>
 </ul>
 <Link to="/guide#documents" className="inline-flex items-center gap-1 mt-3 text-[#1B6C62] hover:underline" data-testid="empty-docs-guide-link">
 Read the documents guide <ArrowLeft size={11} strokeWidth={1.5} className="rotate-180" />
 </Link>
 </div>
 </div>
 ) : visibleDocs.length === 0 ? (
              <div className="card text-center py-10 bg-[#FAF9F6]" data-testid="tag-filter-empty">
                <p className="font-serif text-lg text-[#0D2B52] mb-1">No documents tagged &ldquo;{activeTag?.name}&rdquo; yet</p>
                <p className="text-sm text-[#6B7280] max-w-sm mx-auto mb-3">Clear the filter to see everything, or select some documents and add them to this tag.</p>
                <button
                  type="button"
                  onClick={() => setActiveTagId(null)}
                  className="btn-secondary text-xs"
                  data-testid="tag-filter-empty-clear"
                >
                  Show all documents
                </button>
              </div>
            ) : visibleDocs.map(d => {
 const isSel = selected.has(d.id);
 const docGroups = groupsByDoc[d.id] || [];
 return (
 <div
 key={d.id}
 className={`card p-5 flex gap-4 items-start ${selectMode ? "cursor-pointer" : ""} ${isSel ? "ring-2 ring-[#1B6C62] bg-[#FAFCFB]" : ""}`}
 data-testid={`doc-${d.id}`}
 onClick={selectMode ? (e) => { if (e.target.closest("button") || e.target.closest("a")) return; toggleSelect(d.id); } : undefined}
 >
 {selectMode && (
 <button
 onClick={(e) => { e.stopPropagation(); toggleSelect(d.id); }}
 className="mt-1 flex-shrink-0"
 aria-label={isSel ? "Deselect" : "Select"}
 data-testid={`doc-select-${d.id}`}
 >
 {isSel ? (
 <CheckSquare size={20} strokeWidth={1.75} className="text-[#1B6C62]" />
 ) : (
 <SquareIcon size={20} strokeWidth={1.5} className="text-[#6B7280]" />
 )}
 </button>
 )}
 <div className="w-12 h-12 flex flex-col items-center justify-center bg-[#FAF9F6] border border-[#E5E4DE] flex-shrink-0">
 <span className="font-mono text-[8px] text-[#1B6C62] leading-none tracking-wider">ANNEX</span>
 <span className="font-serif text-lg text-[#0D2B52] leading-tight mt-0.5">{byId[d.id] || ","}</span>
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex justify-between items-start gap-3">
 <div className="min-w-0">
 <div className="label-mono mb-0.5">{docTypeLabel(d.doc_type)} {d.event_date && `· ${d.event_date.slice(0,10)}`}</div>
 <h4 className="font-medium text-[#1A1A1A] truncate">{d.title || d.original_name}</h4>
                    {docGroups.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`doc-${d.id}-groups`}>
                        {docGroups.map((g) => {
                          const c = TAG_COLOR_STYLES[g.color]?.dot || "bg-[#1B6C62]";
                          return (
                            <span
                              key={g.id}
                              className={`inline-flex items-center gap-1 ${c} text-white text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5`}
                              title={`In group: ${g.name}`}
                              data-testid={`doc-${d.id}-group-${g.id}`}
                            >
                              <Layers size={9} strokeWidth={2} />
                              {g.name}
                            </span>
                          );
                        })}
                      </div>
                    )}
 </div>
 <div className="flex gap-1 flex-shrink-0">
 <button onClick={(e) => { e.stopPropagation(); setViewerDoc(d); }} className="btn-ghost p-1.5" title="View and highlight" data-testid={`view-${d.id}`}><Highlighter size={15} strokeWidth={1.5} /></button>
 <button onClick={(e) => { e.stopPropagation(); download(d); }} className="btn-ghost p-1.5" title="Download" data-testid={`download-${d.id}`}><Download size={15} strokeWidth={1.5} /></button>
 <button onClick={(e) => { e.stopPropagation(); remove(d.id); }} className="btn-ghost p-1.5 hover:text-[#B35D4C]" title="Delete" data-testid={`delete-doc-${d.id}`}><Trash2 size={15} strokeWidth={1.5} /></button>
 </div>
 </div>
 {d.summary && <p className="text-sm text-[#333] mt-2 leading-relaxed font-serif italic">{d.summary}</p>}
 {(d.mime_type || "").startsWith("image/") && (
 <div className="mt-2 flex flex-wrap items-center gap-2">
 {d.metadata && (d.metadata.captured_at || d.metadata.gps_lat) && (
 <div className="inline-flex items-center gap-2 bg-[#E6F5F2] border border-[#1B6C62]/30 px-2.5 py-1 text-[11px] font-mono text-[#0D2B52]" data-testid={`exif-${d.id}`} title="From the file's embedded EXIF metadata">
 <Camera size={12} strokeWidth={1.5} className="text-[#1B6C62]" />
 {d.metadata.captured_at && <span>Taken: {d.metadata.captured_at}</span>}
 {d.metadata.Make && d.metadata.Model && <span className="text-[#6B7280]">· {d.metadata.Make} {d.metadata.Model}</span>}
 {d.metadata.gps_lat && <span className="text-[#6B7280]">· GPS {d.metadata.gps_lat.toFixed(3)}, {d.metadata.gps_lon.toFixed(3)}</span>}
 </div>
 )}
 <ForensicCheck doc={d} />
 </div>
 )}
 <p className="text-xs text-[#6B7280] mt-1.5">{(d.size_bytes / 1024).toFixed(0)} KB · uploaded {d.created_at?.slice(0,10)}{d.date_source && d.event_date && <span className="italic"> · date {d.date_source.replace(/_/g, ' ')}</span>}</p>
 <DeepReadPanel doc={d} onUpdate={reload} />
 <DocSummary doc={d} onUpdate={reload} />
 </div>
 </div>
 );
 })}
 </div>

 {/* Floating selection toolbar (visible when 1+ docs selected). */}
 {selectMode && selected.size > 0 && (
 <div
 className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 bg-[#0D2B52] text-white shadow-2xl border border-[#0D2B52] flex items-center gap-3 px-4 py-2.5"
 data-testid="docs-selection-bar"
 >
 <span className="font-mono text-[11px] tracking-wider uppercase text-white/70">
 {selected.size} selected
 </span>
 <button
 onClick={() => setGroupModalOpen(true)}
 className="bg-white text-[#0D2B52] hover:bg-[#FAF9F6] text-sm font-medium px-3 py-1.5 inline-flex items-center gap-1.5"
 data-testid="docs-group-selected"
 >
 <Layers size={13} strokeWidth={1.75} /> Group selected
 </button>
 <button onClick={clearSelection} className="text-white/80 hover:text-white p-1" aria-label="Clear selection" data-testid="docs-selection-clear">
 <X size={14} strokeWidth={2} />
 </button>
 </div>
 )}

 {/* Name-the-group modal */}
 {groupModalOpen && (
 <div
 className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
 onClick={() => !creatingGroup && setGroupModalOpen(false)}
 data-testid="group-create-modal"
 >
 <div
 className="bg-[#FAF9F6] border border-[#E5E4DE] max-w-sm w-full p-6 shadow-xl"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="flex items-center gap-2 mb-3">
 <Layers size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
 <h3 className="font-serif text-xl text-[#0D2B52]">Group {selected.size} document{selected.size === 1 ? "" : "s"}</h3>
 </div>
 <p className="text-[12px] text-[#6B7280] mb-4 leading-snug">
 Give this bundle a clear name. Group e.g. &ldquo;Inspection 3 evidence&rdquo; or &ldquo;Bond dispute, Sept 2025&rdquo;. You can rename or delete later.
 </p>
 <input
 type="text"
 value={groupName}
 onChange={(e) => setGroupName(e.target.value)}
 onKeyDown={(e) => { if (e.key === "Enter") createGroup(); }}
 autoFocus
 maxLength={80}
 placeholder="Group name"
 className="input-field w-full mb-3"
 data-testid="group-name-input"
 />
              <div className="mb-5">
              <label className="label-mono block mb-2">Colour</label>
              <div className="grid grid-cols-5 gap-2" data-testid="group-color-swatches">
              {[
              { id: "teal",   hex: "#1B6C62" },
              { id: "navy",   hex: "#0D2B52" },
              { id: "gold",   hex: "#C9A961" },
              { id: "rose",   hex: "#B35D4C" },
              { id: "slate",  hex: "#6B7280" },
              { id: "purple", hex: "#7C3AED" },
              { id: "green",  hex: "#15803D" },
              { id: "red",    hex: "#B91C1C" },
              { id: "orange", hex: "#C2410C" },
              { id: "cyan",   hex: "#0891B2" },
              ].map((c) => (
 <button
 key={c.id}
 onClick={() => setGroupColor(c.id)}
 className={`w-7 h-7 border-2 ${groupColor === c.id ? "border-[#1A1A1A]" : "border-transparent"}`}
 style={{ background: c.hex }}
 aria-label={`Colour ${c.id}`}
 data-testid={`group-color-${c.id}`}
 />
 ))}
 </div>
 </div>
 <div className="flex justify-end gap-2">
 <button
 onClick={() => setGroupModalOpen(false)}
 disabled={creatingGroup}
 className="btn-secondary text-sm"
 >
 Cancel
 </button>
 <button
 onClick={createGroup}
 disabled={creatingGroup || !groupName.trim()}
 className="btn-teal text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
 data-testid="group-create-confirm"
 >
 {creatingGroup ? <Loader2 size={13} strokeWidth={1.75} className="animate-spin" /> : <Layers size={13} strokeWidth={1.75} />}
 Create group
 </button>
 </div>
 </div>
 </div>
 )}

 {bulkResult && <BulkImportModal caseId={caseId} initialResult={bulkResult} onClose={() => { setBulkResult(null); reload(); }} onDone={reload} />}
 {viewerDoc && <DocumentViewerModal doc={viewerDoc} onClose={() => setViewerDoc(null)} />}
 </div>
 );
}

function TimelineTab({ caseId, caseObj, docs, reloadDocs, reloadCase }) {
 const { user } = useAuth();
 const [items, setItems] = useState([]);
 const [showAdd, setShowAdd] = useState(false);
 const [form, setForm] = useState({ title: "", description: "", event_date: "" });
 const [building, setBuilding] = useState(false);
const [burnHighlights, setBurnHighlights] = useState(false);
 // Desktop-only Ruler/Grid toggle. Default = "grid" (the calendar view is
 // easier to scan at a glance; owner feedback Feb 2026). Persisted per-user
 // via localStorage so anyone who has explicitly chosen "ruler" keeps it.
 const [view, setView] = useState(() => {
 try { return localStorage.getItem("tt_timeline_view") || "grid"; } catch { return "grid"; }
 });
 const setViewPersist = (v) => {
 setView(v);
 try { localStorage.setItem("tt_timeline_view", v); } catch { /* ignore */ }
 };
 const locked = !!caseObj?.chronology_locked;

 const load = () => api.get(`/cases/${caseId}/timeline`).then(({data}) => setItems(data));
 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => { load(); }, [caseId, docs]);

 const toggleLock = async () => {
 try {
 await api.post(`/cases/${caseId}/chronology/${locked ? "unlock" : "lock"}`);
 await reloadCase?.();
 } catch (e) {
 alert(e.response?.data?.detail || "Couldn't change lock state");
 }
 };

 const add = async (e) => {
 e.preventDefault();
 await api.post(`/cases/${caseId}/timeline`, form);
 setForm({ title: "", description: "", event_date: "" });
 setShowAdd(false);
 load();
 };

 const buildBundle = async () => {
 setBuilding(true);
 try {
 const fd = new FormData();
 if (burnHighlights) fd.append("burn_highlights", "1");
 const res = await api.post(`/cases/${caseId}/evidence-bundle`, fd, {
 responseType: "blob",
 validateStatus: (s) => s === 402 || (s >= 200 && s < 300),
 });
 if (res.status === 402) {
 let detail = "Evidence bundles are part of paid plans. Upgrade to download a tribunal-ready PDF.";
 try {
 const text = await res.data.text();
 const parsed = JSON.parse(text);
 if (parsed.detail) detail = parsed.detail;
 } catch {/* ignore */}
 toast.error("Bundle PDF needs a paid plan", {
 description: detail,
 action: { label: "See plans", onClick: () => { window.location.href = "/pricing"; } },
 duration: 9000,
 });
 return;
 }
 const url = window.URL.createObjectURL(res.data);
 const a = document.createElement("a");
 a.href = url;
 const cd = res.headers["content-disposition"] || "";
 const m = cd.match(/filename="?([^";]+)"?/);
 a.download = m ? m[1] : `TenantTrail_Bundle.pdf`;
 document.body.appendChild(a);
 a.click();
 document.body.removeChild(a);
 window.URL.revokeObjectURL(url);
 } catch (e) {
 toast.error(e.response?.data?.detail || "Bundle build failed. make sure you have uploaded at least one document.");
 } finally { setBuilding(false); }
 };

 return (
 <div data-testid="timeline-tab">
 <div className="card bg-[#E6F5F2]/40 mb-6">
 <div className="flex items-start gap-3">
 <Package size={20} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5" />
 <div className="flex-1">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Tribunal-ready evidence bundle</h3>
 <p className="text-sm text-[#333] leading-relaxed mb-3">
 When you're ready to file, generate a single PDF with a cover page, indexed table of contents,
 and every uploaded document appended in chronological order. If your case has a Master File, an executive
 summary of your strongest evidence leads the bundle. Ready to attach to your {stateTribunal(caseObj?.state || "QLD")} application.
 </p>
 {(user?.tier === "free") ? (
 <LockedAction locked={true} reason="bundle" tier="vault" label="Build evidence bundle PDF" testid="build-bundle-locked">
 <span className="btn-primary text-sm inline-flex items-center gap-2 pointer-events-none">
 <Package size={14} strokeWidth={1.5} /> Build evidence bundle PDF
 </span>
 </LockedAction>
 ) : (
 <div className="flex flex-col sm:flex-row sm:items-center gap-3">
 <button onClick={buildBundle} disabled={building} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="build-bundle-btn">
 <Package size={14} strokeWidth={1.5} /> {building ? "Building PDF…" : "Build evidence bundle PDF"}
 </button>
 <label className="inline-flex items-center gap-2 text-xs text-[#333] cursor-pointer select-none" data-testid="bundle-burn-highlights-label">
 <input
 type="checkbox"
 checked={burnHighlights}
 onChange={(e) => setBurnHighlights(e.target.checked)}
 className="w-4 h-4 accent-[#1B6C62]"
 data-testid="bundle-burn-highlights"
 />
 <span>Burn my highlights onto the exported pages</span>
 </label>
 </div>
 )}
 </div>
 </div>
 </div>

 {docs && docs.length > 0 && (
 <div className="card mb-6">
 {/* Desktop-only view toggle. Mobile always uses the ruler (grid is too cramped). */}
 <div className="hidden lg:flex items-center justify-between mb-4 pb-3 border-b border-[#E5E4DE]">
 <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">
 {view === "grid" ? "Calendar view" : "Ruler view"}
 </div>
 <div className="inline-flex border border-[#E5E4DE]" data-testid="timeline-view-toggle">
 <button
 type="button"
 onClick={() => setViewPersist("ruler")}
 className={`px-3 py-1 font-mono text-[10px] tracking-[0.15em] uppercase ${view === "ruler" ? "bg-[#1B6C62] text-white" : "bg-white text-[#6B7280] hover:text-[#0D2B52]"}`}
 data-testid="timeline-view-ruler"
 >
 Ruler
 </button>
 <button
 type="button"
 onClick={() => setViewPersist("grid")}
 className={`px-3 py-1 font-mono text-[10px] tracking-[0.15em] uppercase border-l border-[#E5E4DE] ${view === "grid" ? "bg-[#1B6C62] text-white" : "bg-white text-[#6B7280] hover:text-[#0D2B52]"}`}
 data-testid="timeline-view-grid"
 >
 Calendar grid
 </button>
 </div>
 </div>
 {view === "grid" ? (
 <div className="hidden lg:block">
 <TimelineGrid items={items} onItemClick={(it) => {
 const el = document.querySelector(`[data-testid="timeline-item-${it.id}"]`);
 if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
 }} />
 </div>
 ) : null}
 {/* Ruler view: always shown on mobile/tablet, and on desktop when ruler is selected */}
 <div className={view === "grid" ? "lg:hidden" : ""}>
 <TimelineRuler
 caseId={caseId}
 docs={docs}
 onChange={reloadDocs}
 locked={locked}
 onToggleLock={toggleLock}
 />
 </div>
 </div>
 )}

 <div className="flex justify-between items-center mb-6">
 <p className="text-sm text-[#6B7280]">Documents with dates are placed automatically. Add other events below.</p>
 <button onClick={() => setShowAdd(!showAdd)} className="btn-secondary text-sm" data-testid="add-event-btn">+ Add event</button>
 </div>

 {showAdd && (
 <form onSubmit={add} className="card mb-6 space-y-3" data-testid="add-event-form">
 <input required className="input-field" placeholder="Event title. e.g. Phone call with agent" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="event-title" />
 <input required type="date" className="input-field" value={form.event_date} onChange={e=>setForm({...form,event_date:e.target.value})} data-testid="event-date" />
 <textarea className="input-field" rows={2} placeholder="What happened (optional)" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} data-testid="event-desc" />
 <div className="flex gap-2 justify-end">
 <button type="button" onClick={()=>setShowAdd(false)} className="btn-ghost text-sm">Cancel</button>
 <button type="submit" className="btn-primary text-sm" data-testid="submit-event">Add to timeline</button>
 </div>
 </form>
 )}

 {items.length === 0 ? (
 <div className="card text-center py-12 bg-[#FAF9F6]" data-testid="timeline-empty">
 <Clock size={28} strokeWidth={1.5} className="mx-auto text-[#1B6C62] mb-3 opacity-70" />
 <p className="font-serif text-lg text-[#0D2B52] mb-1">Your chronology builds itself</p>
 <p className="text-sm text-[#6B7280] max-w-md mx-auto mb-4">
 Documents that have a date, like leases, emails and photos with EXIF, slot in automatically. Add phone calls and conversations using <span className="font-medium text-[#0D2B52]">+ Add event</span> above.
 </p>
 <Link to="/guide#chronology" className="text-xs text-[#1B6C62] hover:underline inline-flex items-center gap-1" data-testid="empty-timeline-guide-link">
 How the chronology works →
 </Link>
 </div>
 ) : (
 <div className="relative pl-8">
 <div className="timeline-line" />
 {items.map((it, idx) => {
 const docMatch = it.kind === "document" ? (docs || []).find((d) => d.id === it.document_id) : null;
 return (
 <div key={it.id + idx} className="relative pl-6 pb-8" data-testid={`timeline-item-${it.id}`}>
 <div className="timeline-dot" />
 <div className="font-mono text-xs text-[#6B7280] mb-1">{(it.date || "").slice(0,10)}</div>
 <div className="card p-4">
 <div className="label-mono mb-1">{it.kind === "document" ? docTypeLabel(it.doc_type) : "Event"}</div>
 <h4 className="font-serif text-lg text-[#1A1A1A]">{it.title}</h4>
 {it.description && <p className="text-sm text-[#333] mt-1 leading-relaxed">{it.description}</p>}
 {docMatch && docMatch.doc_type === "voice_note" && (
 <VoiceNoteTimelineExtras doc={docMatch} onUpdated={() => reloadDocs()} />
 )}
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
}

function VoiceRecorder({ caseId, onUploaded }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="btn-ghost inline-flex items-center gap-2 text-sm"
        data-testid="voice-record-btn"
      >
        <Mic size={14} strokeWidth={1.5} /> Voice note
      </button>
      {open && (
        <VoiceNoteRecorder
          caseId={caseId}
          onUploaded={onUploaded}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ChatTab({ caseId, caseState, reloadDocs, docs, onGoToLetters }) {
  const { user, refreshUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef();
  // Tag scoping: when a tag is picked, Compass restricts its document
  // context to just that tag's members. Fetched lazily on mount; the
  // dropdown only surfaces if the case has at least one tag.
  const [groups, setGroups] = useState([]);
  const [scopedGroupId, setScopedGroupId] = useState(null);
  const [hasMasterFile, setHasMasterFile] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/cases/${caseId}/groups`).then(({ data }) => {
      if (alive) setGroups(data || []);
    }).catch(() => {});
    api.get(`/cases/${caseId}/master-file`).then(({ data }) => {
      if (alive) setHasMasterFile(!!data.exists);
    }).catch(() => {});
    return () => { alive = false; };
  }, [caseId]);
  const scopedGroup = useMemo(
    () => (scopedGroupId ? groups.find((g) => g.id === scopedGroupId) : null),
    [scopedGroupId, groups],
  );

  // "Save this answer as evidence" — turns a Compass assistant reply into
  // a timestamped PDF stored under the case. If a tag is currently active,
  // files under that tag automatically (matches what the user asked about).
  const [savingMsgId, setSavingMsgId] = useState(null);
  const [savedMsgIds, setSavedMsgIds] = useState(new Set());
  const saveAsEvidence = async (msg) => {
    if (savingMsgId) return;
    setSavingMsgId(msg.id);
    try {
      const body = { group_id: scopedGroupId || undefined };
      const { data: doc } = await api.post(`/cases/${caseId}/chat/${msg.id}/save-as-evidence`, body);
      setSavedMsgIds((prev) => new Set(prev).add(msg.id));
      await reloadDocs?.();
      const tagLine = scopedGroup ? `Tagged as "${scopedGroup.name}".` : "Untagged. Tag it from the Documents tab.";
      toast.success("Compass answer saved to your case.", {
        description: `${doc.title || "Compass answer"} \u00b7 ${tagLine}`,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save this answer. Try again.");
    } finally {
      setSavingMsgId(null);
    }
  };


 // Build annex map (annex_number → doc) from chronologically sorted docs
 const annexMap = React.useMemo(() => {
 const sorted = [...(docs || [])].sort((a, b) =>
 (a.event_date || a.created_at || "").localeCompare(b.event_date || b.created_at || "")
 );
 const m = {};
 sorted.forEach((d, i) => { m[i + 1] = d; });
 return m;
 }, [docs]);

 const load = () => api.get(`/cases/${caseId}/chat`).then(({data}) => setMessages(data));
 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => { load(); }, [caseId]);
 useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

 const send = async (e) => {
 e?.preventDefault();
 if (!input.trim() || sending) return;
 const text = input.trim();
 setInput("");
 setSending(true);
 setMessages(m => [...m, { id: "tmp", role: "user", content: text, citations: [] }]);
 try {
 await api.post(`/cases/${caseId}/chat`, { message: text, group_id: scopedGroupId || undefined });
 await load();
 await refreshUser();
 } catch (e) {
 alert(e.response?.data?.detail || "AI request failed");
 load();
 } finally { setSending(false); }
 };

 const remaining = (user?.ai_queries_limit || 0) - (user?.ai_queries_used || 0);
 const isDeepAI = user?.model_quality === "sonnet";
 const modelLabel = isDeepAI ? "Deep AI" : "Quick AI";

 return (
 <div className="grid lg:grid-cols-3 gap-6" data-testid="chat-tab">
 <div className="lg:col-span-2">
 <UpgradeNudge
 variant="compass"
 title="You're using Quick AI"
 description="Quick AI gives you accurate, high-level overviews. Upgrade to Deep AI for full step-by-step procedures, your documents read in detail and letters and tribunal forms drafted for you. 100 Q&As/mo for AUD$19.99."
 ctaLabel="Upgrade to Deep AI"
 className="mb-4"
 />
 {!hasMasterFile && (
 <div className="flex items-center justify-between gap-3 flex-wrap border border-[#C9A961]/40 bg-[#FAF6E8] px-3 py-2.5 mb-4" data-testid="master-file-nudge">
 <span className="text-[12.5px] font-serif text-[#0D2B52] inline-flex items-start gap-2 leading-snug">
 <FileSearch size={14} strokeWidth={1.75} className="text-[#8A6D3B] mt-0.5 shrink-0" />
 <span>Compass answers get sharper with a Deep Scan. It builds a Master File of every fact, date and finding in your case, then every chat stands on it.</span>
 </span>
 {["copilot", "dispute", "pro"].includes(user?.tier) ? (
 <button
 onClick={onGoToLetters}
 className="shrink-0 font-mono text-[10.5px] uppercase tracking-[0.12em] text-white bg-[#8A6D3B] px-3 py-1.5 hover:opacity-90"
 data-testid="master-file-nudge-cta"
 >
 Run a Deep Scan →
 </button>
 ) : (
 <Link
 to="/pricing?upgrade=letter_deep_scan"
 className="shrink-0 font-mono text-[10.5px] uppercase tracking-[0.12em] text-white bg-[#8A6D3B] px-3 py-1.5 hover:opacity-90"
 data-testid="master-file-nudge-cta"
 >
 Unlock with Shield →
 </Link>
 )}
 </div>
 )}
 <div className="card flex flex-col" style={{ minHeight: "60vh" }}>
 <div className="flex items-center justify-between pb-4 border-b border-[#E5E4DE] mb-4">
 <div className="flex items-center gap-2">
 <Sparkles size={18} strokeWidth={1.5} className="text-[#0D2B52]" />
 <h3 className="font-serif text-lg">Compass</h3>
 <span className="label-mono text-[10px]">{modelLabel}</span>
 {hasMasterFile && (
 <span className="inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#8A6D3B] bg-[#FAF6E8] border border-[#C9A961]/40 px-1.5 py-0.5" title="Compass answers are grounded in your case Master File, built by your last Deep Scan." data-testid="chat-master-file-chip">
 <FileSearch size={10} strokeWidth={2} /> Master File
 </span>
 )}
 </div>
 <div className="label-mono">{remaining} queries left</div>
 </div>

 <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="chat-messages">
 {messages.length === 0 && (
 <div className="text-center py-10">
 <p className="font-serif text-lg text-[#1A1A1A] mb-2">Ask me anything about your tenancy.</p>
 <p className="text-sm text-[#6B7280] mb-6">I've read the {stateName(caseState)} tenancy Act and your uploaded documents. I'll cite section numbers. and signpost you to the right tribunal ({stateTribunal(caseState)}) when it gets serious.</p>
 <div className="grid sm:grid-cols-2 gap-2 max-w-lg mx-auto">
 {[
 "How do I open a tribunal case?",
 "What notice can my landlord give to end the lease?",
 "Summarise my lease. anything unusual?",
 "What's the bond return process?",
 ].map((s) => (
 <button key={s} onClick={() => setInput(s)} className="text-left text-sm border border-[#E5E4DE] hover:border-[#0D2B52] hover:bg-[#FAF9F6] p-3 transition-colors rounded-sm" data-testid={`suggestion-${s.slice(0,10)}`}>
 {s}
 </button>
 ))}
 </div>
 </div>
 )}
 {messages.map((m) => (
 <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
 <div className={m.role === "user" ? "user-bubble max-w-[85%]" : "ai-bubble max-w-[92%]"} data-testid={`msg-${m.role}`}>
 {m.role === "assistant" && (
 <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
 <div className="label-mono text-[#0D2B52]">Compass</div>
 {!isDeepAI && (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E6F5F2] text-[#0D2B52] font-mono text-[10px] tracking-wider uppercase border border-[#1B6C62]/30" data-testid={`chat-quick-overview-${m.id}`}>
 <Sparkles size={10} strokeWidth={1.75} className="text-[#1B6C62]" /> Quick overview
 </span>
 )}
 </div>
 )}
 {m.role === "assistant"
 ? <RichText text={m.content} annexMap={annexMap} state={caseState} />
 : m.content}
 {m.role === "assistant" && m.id !== "tmp" && (
   <div className="mt-2 flex items-center gap-2 flex-wrap">
     <button
       type="button"
       onClick={() => saveAsEvidence(m)}
       disabled={savingMsgId === m.id || savedMsgIds.has(m.id)}
       className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors disabled:opacity-60 disabled:cursor-default ${
         savedMsgIds.has(m.id)
           ? "border-[#1B6C62] text-[#1B6C62]"
           : "border-[#0D2B52]/40 text-[#0D2B52] hover:bg-[#0D2B52] hover:text-white hover:border-[#0D2B52]"
       }`}
       data-testid={`chat-save-evidence-${m.id}`}
       title="Save this answer as a tribunal-friendly PDF in your case"
     >
       {savedMsgIds.has(m.id) ? (
         <><Check size={11} strokeWidth={2} /> Saved as evidence</>
       ) : savingMsgId === m.id ? (
         "Saving…"
       ) : (
         <><ClipboardCheck size={11} strokeWidth={1.75} /> Save this answer as evidence</>
       )}
     </button>
     {savedMsgIds.has(m.id) && scopedGroup && (
       <span className="text-[10px] font-mono text-[#6B7280]">tagged {scopedGroup.name}</span>
     )}
   </div>
 )}
 {m.role === "assistant" && !isDeepAI && (
 <div className="mt-3 p-3 bg-[#FAF7F0] border-l-2 border-[#1B6C62] flex flex-wrap items-start justify-between gap-2" data-testid={`chat-deep-upgrade-${m.id}`}>
 <div className="flex items-start gap-2 max-w-md">
 <Sparkles size={13} strokeWidth={1.75} className="text-[#1B6C62] mt-0.5 flex-shrink-0" />
 <div className="text-xs text-[#0D2B52] font-serif leading-snug">
 <strong>This is the quick overview.</strong> For a full step-by-step tailored to your situation, with your documents read and your letters and forms drafted for you, upgrade to Deep AI.
 </div>
 </div>
 <Link to="/pricing" className="btn-primary text-[11px] whitespace-nowrap" data-testid={`chat-deep-upgrade-cta-${m.id}`}>See Deep AI plans</Link>
 </div>
 )}
 {m.role === "assistant" && m.dispute_nudge && user?.tier === "free" && (
 <DisputeModeNudge testid={`chat-dispute-nudge-${m.id}`} />
 )}
 </div>
 </div>
 ))}
 {sending && (
 <div className="ai-bubble max-w-[80%]">
 <AIThinking
 compact
 phrases={["Reading your documents", "Cross-referencing the Act", "Drafting a response", "Citing the relevant sections"]}
 testid="chat-loading"
 />
 </div>
 )}
 <div ref={endRef} />
 </div>

          {groups.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#E5E4DE] flex flex-wrap items-center gap-1.5" data-testid="chat-scope-bar">
              <span className="label-mono text-[#6B7280] mr-1">Ask about</span>
              <button
                type="button"
                onClick={() => setScopedGroupId(null)}
                className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors ${
                  scopedGroupId === null
                    ? "bg-[#0D2B52] text-white border-[#0D2B52]"
                    : "bg-white text-[#0D2B52] border-[#E5E4DE] hover:border-[#0D2B52]"
                }`}
                data-testid="chat-scope-all"
              >
                Whole case
              </button>
              {groups.map((g) => {
                const styles = TAG_COLOR_STYLES[g.color] || TAG_COLOR_STYLES.teal;
                const active = scopedGroupId === g.id;
                const count = (g.doc_ids || []).length;
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setScopedGroupId(active ? null : g.id)}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors ${
                      active ? `${styles.pill} border-transparent` : "bg-white text-[#1A1A1A] border-[#E5E4DE] hover:border-[#0D2B52]"
                    }`}
                    title={`Scope Compass to ${g.name} (${count} doc${count === 1 ? "" : "s"})`}
                    data-testid={`chat-scope-${g.id}`}
                  >
                    <span className={`w-2 h-2 ${styles.dot} ${active ? "ring-1 ring-white/70" : ""}`} />
                    {g.name} ({count})
                  </button>
                );
              })}
            </div>
          )}

          <form onSubmit={send} className={`${groups.length > 0 ? "mt-3" : "mt-4"} ${groups.length > 0 ? "" : "pt-4 border-t border-[#E5E4DE]"} flex gap-2`} data-testid="chat-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={scopedGroup ? `Ask about your "${scopedGroup.name}" documents…` : "Ask about your case…"}
              className="input-field flex-1"
              disabled={sending || remaining <= 0}
              data-testid="chat-input"
            />
            <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={sending || !input.trim() || remaining <= 0} data-testid="chat-send">
              <Send size={15} strokeWidth={1.5} /> Send
            </button>
          </form>
 {remaining <= 0 && (
 <p className="text-xs text-[#B35D4C] mt-2 text-center">
 You've used all queries this period. <Link to="/pricing" className="underline">Upgrade your plan</Link>.
 </p>
 )}
 <div className="flex justify-between items-center mt-3">
 <VoiceRecorder caseId={caseId} onUploaded={reloadDocs} />
 <span className="text-[10px] text-[#6B7280] font-serif italic">Replies cite Act sections. Not legal advice.</span>
 </div>
 </div>
 </div>

 <div className="space-y-6">
 <Disclaimer state={caseState} />
 </div>
 </div>
 );
}

export default function CaseDetail() {
 const { id } = useParams();
 const [searchParams, setSearchParams] = useSearchParams();
 const initialTab = searchParams.get("tab") || "documents";
 const [caseObj, setCaseObj] = useState(null);
 // `loadError` is null while loading-or-ok, "notfound" when the API returns
 // 404 (deleted case, typo in URL, stale link, etc) and "error" for any other
 // failure. Stops the page from being stuck on "Loading case…" forever.
 const [loadError, setLoadError] = useState(null);
 const [docs, setDocs] = useState([]);
 const [tab, setTabRaw] = useState(initialTab);
 const [voiceOpen, setVoiceOpen] = useState(false);

 const setTab = (next) => {
 setTabRaw(next);
 const sp = new URLSearchParams(searchParams);
 if (next === "documents") sp.delete("tab"); else sp.set("tab", next);
 setSearchParams(sp, { replace: true });
 };

 const [tabIntent, setTabIntent] = useState(null); // optional intent passed from an insight click → DocumentsTab preselects doc_type, etc.
 const [nswCheckOpen, setNswCheckOpen] = useState(false);
 const [nswCheckQuery, setNswCheckQuery] = useState("");

 const addressInsight = (action) => {
 if (action?.kind === "open_tab" && action.target) setTab(action.target);
 else if (action?.kind === "open_document") setTab("documents");
 else if (action?.kind === "open_nsw_check") {
 setNswCheckQuery(action.target || "");
 setNswCheckOpen(true);
 return; // don't scroll
 }
 if (action?.intent) setTabIntent(action.intent);
 // Scroll the tab content into view so the user sees the switch took effect.
 setTimeout(() => {
 document.querySelector('[data-testid="tab-content-anchor"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
 }, 50);
 };

 const reloadDocs = () => api.get(`/cases/${id}/documents`).then(({data}) => setDocs(data)).catch(() => {});
 const reloadCase = () =>
 api
 .get(`/cases/${id}`)
 .then(({ data }) => {
 setCaseObj(data);
 setLoadError(null);
 })
 .catch((err) => {
 setLoadError(err?.response?.status === 404 ? "notfound" : "error");
 });

 useEffect(() => {
 reloadCase();
 reloadDocs();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [id]);

 // Refresh docs when the floating mic button uploads a voice note for THIS case.
 useEffect(() => {
   const onAdded = (e) => {
     if (e.detail?.caseId === id) reloadDocs();
   };
   window.addEventListener("tt:voice-note-added", onAdded);
   return () => window.removeEventListener("tt:voice-note-added", onAdded);
    
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [id]);

 // Honour URL changes while on the page (e.g. clicking a notification while
 // already viewing the case).
 useEffect(() => {
 const t = searchParams.get("tab");
 if (t && t !== tab) setTabRaw(t);
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [searchParams]);

 if (loadError === "notfound") {
 return (
 <div className="min-h-screen paper-grain">
 <Navbar />
 <div className="max-w-2xl mx-auto px-6 py-16" data-testid="case-not-found">
 <div className="label-mono mb-3">404 &middot; Case not found</div>
 <h1 className="font-serif text-3xl text-[#0D2B52] mb-3">We couldn&rsquo;t find that case.</h1>
 <p className="text-[14px] text-[#5a6b7a] leading-relaxed mb-6">
 It may have been deleted, the link could be stale, or the address has a typo. Your other cases are safe on your dashboard.
 </p>
 <div className="flex flex-wrap gap-3">
 <Link to="/dashboard" className="btn-teal inline-flex items-center gap-2" data-testid="case-not-found-back">
 <ArrowLeft size={14} strokeWidth={1.75} /> Back to your cases
 </Link>
 <Link to="/dashboard?new=1" className="btn-secondary inline-flex items-center gap-2" data-testid="case-not-found-new">
 Start a new case
 </Link>
 </div>
 </div>
 </div>
 );
 }
 if (loadError === "error") {
 return (
 <div className="min-h-screen paper-grain">
 <Navbar />
 <div className="max-w-2xl mx-auto px-6 py-16" data-testid="case-load-error">
 <div className="label-mono mb-3">Something went wrong</div>
 <h1 className="font-serif text-3xl text-[#0D2B52] mb-3">We couldn&rsquo;t load this case right now.</h1>
 <p className="text-[14px] text-[#5a6b7a] leading-relaxed mb-6">
 The connection may have dropped. Try again in a moment.
 </p>
 <div className="flex flex-wrap gap-3">
 <button
 onClick={() => { setLoadError(null); reloadCase(); }}
 className="btn-teal inline-flex items-center gap-2"
 data-testid="case-load-error-retry"
 >
 Try again
 </button>
 <Link to="/dashboard" className="btn-secondary inline-flex items-center gap-2">
 Back to your cases
 </Link>
 </div>
 </div>
 </div>
 );
 }
 if (!caseObj) return (<div className="min-h-screen paper-grain"><Navbar /><div className="max-w-7xl mx-auto px-6 py-12 text-[#6B7280] italic" data-testid="case-loading">Loading case&hellip;</div></div>);

 const prefs = caseObj.preferences || {};
 const inDispute = !!prefs.in_dispute;

 // Primary tabs are always shown; secondary tabs hide unless their relevance
 // is set in the onboarding wizard preferences.
 const primaryTabs = [
 ["documents", "Documents", FileSearch],
 ["photo-trail", "Photo Trail", Camera],
 ["timeline", "Chronology", Clock],
 ["inspections", "Inspections", ClipboardList],
 ["chat", "Ask Compass", Sparkles],
 ];
 const secondaryTabs = [
 ["letters", "Letters", Mail],
 ["compare", "Photo Compare", GitCompareArrows],
 ["contacts", "Contacts", Phone],
...(prefs.tracks_rent ? [["rent", "Rent ledger", Receipt]] : []),
...(prefs.has_pets ? [["pets", "Pets", PawPrint]] : []),
 ];
 const showOnboarding = caseObj && !caseObj.onboarded;

 return (
 <div className="min-h-screen paper-grain pb-20 md:pb-0">
 <Navbar />
 <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6 sm:py-8">
 <Link to="/dashboard" className="btn-ghost inline-flex items-center gap-1.5 text-sm mb-4" data-testid="back-to-dashboard">
 <ArrowLeft size={14} strokeWidth={1.5} /> Back to cases
 </Link>
 <CaseHeader c={caseObj} />

 <DisagreementsBanner caseId={id} onGoToInspections={() => setTab("inspections")} />

 <InsightsPanel caseId={id} onAddress={addressInsight} />

 <div className="grid lg:grid-cols-3 gap-6 mb-8">
 <div className="lg:col-span-2">
 <DeadlinesWidget caseId={id} />
 </div>
 <div className="card">
 <div className="flex items-center gap-2 mb-2">
 <Mic size={16} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h4 className="font-serif text-lg text-[#0D2B52]">Tell your story</h4>
 </div>
 <p className="text-sm text-[#6B7280] mb-3 leading-snug">
 Record what happened. The AI shapes it into a polished tribunal-ready statement that references your annexes.
 </p>
 <button onClick={() => setVoiceOpen(true)} className="btn-teal text-sm w-full inline-flex items-center justify-center gap-2" data-testid="open-voice-statement">
 <Mic size={14} strokeWidth={1.5} /> Voice → Statement
 </button>
 </div>
 </div>

 <div className="hidden md:block mb-8 overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0 scrollbar-thin" data-testid="case-tabs">
 <div className="inline-flex items-stretch gap-1 p-1 bg-[#EEEFF1] border border-[#D8DCE2] min-w-max">
 {primaryTabs.map(([k, label, Icon]) => {
 const isActive = tab === k;
 return (
 <button
 key={k}
 onClick={() => setTab(k)}
 className={`px-3.5 py-2 text-sm font-medium inline-flex items-center gap-2 shrink-0 transition-colors border ${
 isActive
 ? "bg-white text-[#0D2B52] border-[#0D2B52] shadow-sm"
 : "bg-transparent text-[#5a6b7a] border-transparent hover:bg-white/70 hover:text-[#0D2B52]"
 }`}
 data-testid={`tab-${k}`}
 >
 <Icon size={15} strokeWidth={1.75} className={isActive ? "text-[#1B6C62]" : "text-[#6B7280]"} /> {label}
 </button>
 );
 })}
 <MoreMenu items={secondaryTabs} active={tab} onPick={setTab} />
 </div>
 </div>

 <div data-testid="tab-content-anchor" />
 {tab === "documents" && <DocumentsTab caseId={id} docs={docs} reload={reloadDocs} intent={tabIntent} clearIntent={() => setTabIntent(null)} />}
 {tab === "photo-trail" && <PhotoTrailTab caseId={id} onSwitchTab={setTab} />}
 {tab === "compare" && <PhotoComparisonTab caseId={id} caseState={caseObj.state} />}
 {tab === "timeline" && <TimelineTab caseId={id} caseObj={caseObj} docs={docs} reloadDocs={reloadDocs} reloadCase={reloadCase} />}
 {tab === "inspections" && <InspectionTab caseId={id} />}
 {tab === "rent" && <RentLedgerTab caseId={id} />}
 {tab === "contacts" && <ContactsTab caseId={id} />}
 {tab === "pets" && <PetsTab caseId={id} caseState={caseObj.state} />}
 {tab === "letters" && <LettersTab caseId={id} caseState={caseObj.state} caseObj={caseObj} />}
 {tab === "chat" && <ChatTab caseId={id} caseState={caseObj.state} reloadDocs={reloadDocs} docs={docs} onGoToLetters={() => setTab("letters")} />}
 </div>
 {voiceOpen && <VoiceStatementModal caseId={id} caseState={caseObj.state} onClose={() => setVoiceOpen(false)} />}
 {showOnboarding && <OnboardingWizard caseId={id} onDone={reloadCase} onSkip={reloadCase} />}
 <NswAgentCheck
 triggerless
 open={nswCheckOpen}
 initialQuery={nswCheckQuery}
 onClose={() => setNswCheckOpen(false)}
 />
 {/* Mobile-only fixed bottom nav. replaces the cramped 9-tab scroller */}
 <MobileBottomNav tab={tab} setTab={setTab} secondaryTabs={secondaryTabs} />
 </div>
 );
}

/** "More ▾" dropdown for secondary tabs. */
function MoreMenu({ items, active, onPick }) {
 const [open, setOpen] = useState(false);
 const ref = useRef(null);
 useEffect(() => {
 const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 if (open) document.addEventListener("mousedown", h);
 return () => document.removeEventListener("mousedown", h);
 }, [open]);

 if (items.length === 0) return null;
 const activeInMore = items.find(([k]) => k === active);

 return (
 <div ref={ref} className="relative">
 <button
 onClick={() => setOpen(o => !o)}
 className={`px-3.5 py-2 text-sm font-medium inline-flex items-center gap-1.5 shrink-0 transition-colors border ${
 activeInMore
 ? "bg-white text-[#0D2B52] border-[#0D2B52] shadow-sm"
 : "bg-transparent text-[#5a6b7a] border-transparent hover:bg-white/70 hover:text-[#0D2B52]"
 }`}
 data-testid="tab-more"
 >
 {activeInMore ? (
 <>
 {React.createElement(activeInMore[2], { size: 15, strokeWidth: 1.75, className: "text-[#1B6C62]" })}
 {activeInMore[1]}
 </>
 ) : (
 <>More</>
 )}
 <ChevronDown size={13} strokeWidth={1.75} className={`transition-transform ${open ? "rotate-180" : ""}`} />
 </button>
 {open && (
 <div className="absolute left-0 top-full mt-1 min-w-[170px] bg-white border border-[#E5E4DE] shadow-lg z-30">
 {items.map(([k, label, Icon]) => (
 <button
 key={k}
 onClick={() => { setOpen(false); onPick(k); }}
 className={`w-full text-left px-3 py-2 text-sm inline-flex items-center gap-2 hover:bg-[#FAF9F6] ${active === k ? "text-[#0D2B52] font-medium" : "text-[#1A1A1A]"}`}
 data-testid={`tab-${k}`}
 >
 <Icon size={14} strokeWidth={1.5} className="text-[#1B6C62]" /> {label}
 </button>
 ))}
 </div>
 )}
 </div>
 );
}
